// ===========================================
// PRE-SHIFT PAGE - ROLE-BASED WIZARD
// ===========================================

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { useShiftStore } from '../stores/shiftStore';
import { shiftApi } from '../services/api';
import { useToast } from '../components/Toast';
import { getRoleShiftConfig, StepType } from '../config/roleShiftConfig';
import { ReservationsStep, KitchenStep, OverviewStep } from '../components/preshift';
import {
  ChefHat,
  Check,
  ChevronRight,
  ChevronLeft,
  Clock,
  Users,
  AlertTriangle,
  Star,
  XCircle,
  AlertCircle,
  MapPin,
  Loader2,
  Sparkles,
  UtensilsCrossed,
  Shield,
  Calendar,
  Flame,
  LayoutDashboard,
  Megaphone,
} from 'lucide-react';
import clsx from 'clsx';

// Step metadata for display
const STEP_CONFIG: Record<StepType, { label: string; icon: typeof ChefHat }> = {
  welcome: { label: 'Welcome', icon: Sparkles },
  menu: { label: 'Menu', icon: UtensilsCrossed },
  allergies: { label: 'Alerts', icon: Shield },
  section: { label: 'Section', icon: MapPin },
  reservations: { label: 'Reservations', icon: Calendar },
  kitchen: { label: 'Kitchen', icon: Flame },
  overview: { label: 'Overview', icon: LayoutDashboard },
  start: { label: 'Start', icon: Clock },
};

function splitTextList(value?: string) {
  if (!value) return [];
  return value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatCurrency(cents?: number) {
  if (typeof cents !== 'number') return null;
  return `$${(cents / 100).toFixed(2)}`;
}

function formatShiftTime(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

export default function PreShiftPage() {
  const navigate = useNavigate();
  const { employee } = useAuthStore();
  const {
    checklist,
    preShiftData,
    selectedSectionId,
    selectedStationId,
    setPreShiftData,
    updateChecklist,
    selectSection,
    selectStation,
    startShift,
    isChecklistComplete,
  } = useShiftStore();

  // Get role-specific configuration. The role someone is WORKING TONIGHT wins
  // over their employee-record role — a cook picking up a server shift preps
  // sections, not stations (and vice versa).
  const employeeRole = preShiftData?.shiftAssignment?.role || employee?.primaryRole || employee?.role || preShiftData?.employee.primaryRole || 'server';
  const roleConfig = useMemo(() => getRoleShiftConfig(employeeRole), [employeeRole]);
  const STEPS = useMemo(() =>
    roleConfig.steps.map(stepId => ({
      id: stepId,
      ...STEP_CONFIG[stepId],
    })),
    [roleConfig]
  );

  const [currentStep, setCurrentStep] = useState<StepType>('welcome');
  const [error, setError] = useState<string | null>(null);
  const { showError } = useToast();

  // Fetch pre-shift data
  const { data: preShiftResponse, isLoading, refetch } = useQuery({
    queryKey: ['pre-shift', employee?.id],
    queryFn: async () => {
      if (!employee?.id) throw new Error('No employee');
      const response = await shiftApi.getPreShiftData(employee.id);
      if (!response.success) throw new Error(response.error?.message || 'Failed to load');
      return response.data;
    },
    enabled: !!employee?.id,
    staleTime: 0,
  });

  // Update store when data loads
  useEffect(() => {
    if (preShiftResponse) {
      setPreShiftData(preShiftResponse);
    }
  }, [preShiftResponse, setPreShiftData]);

  // Assign section mutation
  const assignSectionMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      if (!employee?.id) throw new Error('No employee');
      const response = await shiftApi.updateChecklist(employee.id, { sectionId });
      if (!response.success) throw new Error(response.error?.message || 'Failed to assign section');
      return response.data;
    },
    onSuccess: () => {
      updateChecklist({ sectionConfirmed: true });
      refetch();
    },
    onError: (err: Error) => {
      setError(err.message);
      showError(err.message || 'Failed to assign section. Please try again.');
    },
  });

  // Start shift mutation
  const startShiftMutation = useMutation({
    mutationFn: async () => {
      if (!employee?.id) throw new Error('No employee');
      const response = await shiftApi.startShift(employee.id, {
        checklist: {
          menuReviewed: checklist.menuReviewed,
          allergiesReviewed: checklist.allergiesReviewed,
          sectionConfirmed: checklist.sectionConfirmed,
          reservationsReviewed: checklist.reservationsReviewed,
          eightySixReviewed: checklist.eightySixReviewed,
          stationConfirmed: checklist.stationConfirmed,
          overviewReviewed: checklist.overviewReviewed,
        },
        // Include stationId for cooks
        stationId: selectedStationId || undefined,
        sectionId: selectedSectionId || preShiftData?.assignedSection?.id || undefined,
      });
      if (!response.success) throw new Error(response.error?.message || 'Failed to start shift');
      return response.data;
    },
    onSuccess: (data) => {
      // Handle resumed shifts - get section from response or preShiftData
      let section = data.assignedSection || preShiftData?.assignedSection || preShiftData?.availableSections.find(s => s.id === selectedSectionId);

      startShift(
        data.timeEntry.id,
        section ? { id: section.id, name: section.name, color: section.color } : null,
        section?.tables || []
      );
      navigate('/tables');
    },
    onError: (err: Error) => {
      setError(err.message);
      showError(
        err.message || 'Failed to start shift. Please try again.',
        { label: 'Retry', onClick: () => startShiftMutation.mutate() }
      );
    },
  });

  const handleNext = () => {
    const stepIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[stepIndex + 1].id);
    }
  };

  const handleBack = () => {
    const stepIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex > 0) {
      setCurrentStep(STEPS[stepIndex - 1].id);
    }
  };

  const handleSelectSection = (sectionId: string) => {
    selectSection(sectionId);
    assignSectionMutation.mutate(sectionId);
  };

  const handleStartShift = () => {
    if (!isChecklistComplete(employeeRole)) {
      const message = 'Please complete all checklist items before starting your shift';
      setError(message);
      showError(message);
      return;
    }
    startShiftMutation.mutate();
  };

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading shift preparation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-gradient-to-br from-primary-900 to-primary-700 flex flex-col safe-top safe-bottom overflow-hidden">
      {/* Header */}
      <header className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChefHat className="w-6 h-6 text-white" />
          <span className="text-white font-semibold">Pre-Shift</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              // Skip all checklist items and start shift
              updateChecklist({
                menuReviewed: true,
                allergiesReviewed: true,
                sectionConfirmed: true,
                reservationsReviewed: true,
                eightySixReviewed: true,
                stationConfirmed: true,
                overviewReviewed: true,
              });
              startShiftMutation.mutate();
            }}
            disabled={startShiftMutation.isPending}
            className="text-sm text-primary-200 hover:text-white transition-colors"
          >
            Skip
          </button>
          <div className="text-sm text-primary-200">
            Step {currentStepIndex + 1} of {STEPS.length}
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="px-4 py-2">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isComplete = index < currentStepIndex;
            const isAccessible = index <= currentStepIndex;

            return (
              <button
                key={step.id}
                onClick={() => isAccessible && setCurrentStep(step.id)}
                disabled={!isAccessible}
                className={clsx(
                  'flex flex-col items-center gap-1 transition-all',
                  isActive && 'scale-110',
                  !isAccessible && 'opacity-40'
                )}
              >
                <div
                  className={clsx(
                    'w-10 h-10 rounded-full flex items-center justify-center transition-all',
                    isActive && 'bg-white text-primary-700',
                    isComplete && 'bg-emerald-500 text-white',
                    !isActive && !isComplete && 'bg-white/20 text-white'
                  )}
                >
                  {isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={clsx(
                  'text-[10px] font-medium',
                  isActive ? 'text-white' : 'text-primary-200'
                )}>
                  {step.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto px-4 pb-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Step Content */}
          <div className="p-6">
            {/* WELCOME STEP */}
            {currentStep === 'welcome' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-10 h-10 text-primary-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                  Welcome, {preShiftData?.employee.firstName}!
                </h1>
                <p className="text-slate-500 mb-6">
                  {/* Derive the recap from the role's actual steps (they differ per role) */}
                  Let's prepare for your shift.
                  {STEPS.length > 2 && (
                    <> We'll review: {STEPS
                      .filter(s => s.id !== 'welcome' && s.id !== 'start')
                      .map(s => STEP_CONFIG[s.id].label)
                      .join(', ')}.</>
                  )}
                </p>

                {preShiftData?.shiftInstance && (
                  <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6 text-left">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Assigned shift</p>
                    <p className="text-lg font-semibold text-slate-900 mt-1">{preShiftData.shiftInstance.name}</p>
                    <p className="text-sm text-slate-600 mt-1">
                      {formatShiftTime(preShiftData.shiftAssignment?.scheduledStart || preShiftData.shiftInstance.startTime)}
                      {' - '}
                      {formatShiftTime(preShiftData.shiftAssignment?.scheduledEnd || preShiftData.shiftInstance.endTime)}
                    </p>
                    <p className="text-sm text-primary-700 mt-2 capitalize">
                      Working as {preShiftData.shiftAssignment?.role || employeeRole}
                    </p>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="text-2xl font-bold text-primary-600">
                      {preShiftData?.menu.dailySpecials.length || 0}
                    </div>
                    <div className="text-xs text-slate-500">Specials</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="text-2xl font-bold text-red-600">
                      {preShiftData?.menu.eightySixItems.length || 0}
                    </div>
                    <div className="text-xs text-slate-500">86'd Items</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="text-2xl font-bold text-amber-600">
                      {preShiftData?.guestAlerts.length || 0}
                    </div>
                    <div className="text-xs text-slate-500">Alerts</div>
                  </div>
                </div>

                {/* Announcements */}
                {(preShiftData?.announcements?.length ?? 0) > 0 && (
                  <div className="mb-6 space-y-2">
                    {preShiftData?.announcements.map((ann: any) => {
                      const priorityColors: Record<string, string> = {
                        urgent: 'bg-red-50 border-red-300 text-red-800',
                        high: 'bg-orange-50 border-orange-300 text-orange-800',
                        normal: 'bg-blue-50 border-blue-300 text-blue-800',
                        low: 'bg-slate-50 border-slate-300 text-slate-700',
                      };
                      const colorClass = priorityColors[ann.priority] || priorityColors.normal;

                      return (
                        <div
                          key={ann.id}
                          className={clsx('rounded-xl p-4 border-2', colorClass)}
                        >
                          <div className="flex items-start gap-3">
                            <Megaphone className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold">{ann.title}</p>
                              <p className="text-sm mt-1 opacity-90">{ann.message}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {preShiftData?.assignedSection ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: preShiftData.assignedSection.color }}
                      />
                      <span className="font-semibold text-emerald-800">
                        Assigned: {preShiftData.assignedSection.name}
                      </span>
                    </div>
                    <p className="text-sm text-emerald-700">
                      {preShiftData.assignedSection.tables.length} tables in your section
                    </p>
                  </div>
                ) : roleConfig.requiresSection ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertCircle className="w-5 h-5" />
                      <span className="font-semibold">No section assigned</span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      {/* Steps are role-based, so derive the real step number */}
                      You'll select your section in step {STEPS.findIndex(s => s.id === 'section') + 1}
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            {/* MENU STEP */}
            {currentStep === 'menu' && (
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Today's Menu</h2>
                <p className="text-slate-500 mb-6">Review specials and unavailable items</p>

                {/* Daily Specials */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Daily Specials
                  </h3>
                  {preShiftData?.menu.dailySpecials.length ? (
                    <div className="space-y-2">
                      {preShiftData.menu.dailySpecials.map((item) => (
                        <div key={item.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-slate-900">{item.name}</p>
                              {item.description && (
                                <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                              )}
                              {item.staffDescription && (
                                <p className="text-sm text-amber-800 mt-2">{item.staffDescription}</p>
                              )}
                              {(item.allergenInfo || item.ingredientsList) && (
                                <div className="mt-2 space-y-1">
                                  {item.allergenInfo && (
                                    <p className="text-xs text-red-700">Allergens: {item.allergenInfo}</p>
                                  )}
                                  {item.ingredientsList && (
                                    <p className="text-xs text-slate-500">Ingredients: {item.ingredientsList}</p>
                                  )}
                                </div>
                              )}
                              {(item.pairingSuggestions?.length || splitTextList(item.ingredientsList).length > 0) && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {(item.pairingSuggestions || []).map((pairing) => (
                                    <span
                                      key={pairing}
                                      className="px-2 py-0.5 rounded-full bg-white/80 text-amber-800 text-xs font-medium"
                                    >
                                      Pair with {pairing}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              {formatCurrency(item.priceCents) && (
                                <span className="font-semibold text-slate-900">
                                  {formatCurrency(item.priceCents)}
                                </span>
                              )}
                              {formatCurrency(item.regularPriceCents) && item.regularPriceCents !== item.priceCents && (
                                <p className="text-xs text-slate-500 mt-1 line-through">
                                  {formatCurrency(item.regularPriceCents)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">No specials today</p>
                  )}
                </div>

                {/* 86'd Items */}
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    86'd Items (Unavailable)
                  </h3>
                  {preShiftData?.menu.eightySixItems.length ? (
                    <div className="flex flex-wrap gap-2">
                      {preShiftData.menu.eightySixItems.map((item) => (
                        <div
                          key={item.id}
                          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2"
                        >
                          <p className="text-sm font-medium text-red-700">{item.name}</p>
                          {item.prepNotes && (
                            <p className="mt-1 text-xs text-red-600">{item.prepNotes}</p>
                          )}
                          {item.allergenInfo && (
                            <p className="mt-1 text-xs text-red-700">Allergens: {item.allergenInfo}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">All items available</p>
                  )}
                </div>

                {/* Limited Items */}
                {(preShiftData?.menu.limitedItems?.length ?? 0) > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      Limited Availability
                    </h3>
                    <div className="space-y-2">
                      {preShiftData?.menu.limitedItems.map((item) => (
                        <div key={item.id} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-900 font-medium">{item.name}</span>
                            <span className="text-amber-700 font-semibold">
                              {item.availableQuantity} left
                            </span>
                          </div>
                          {(item.prepNotes || item.staffDescription) && (
                            <p className="mt-1 text-xs text-amber-800">{item.prepNotes || item.staffDescription}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Acknowledge */}
                <button
                  onClick={() => {
                    updateChecklist({ menuReviewed: true });
                    handleNext();
                  }}
                  className={clsx(
                    'w-full mt-6 py-4 rounded-xl font-semibold text-lg transition-all',
                    checklist.menuReviewed
                      ? 'bg-emerald-500 text-white'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  )}
                >
                  {checklist.menuReviewed ? (
                    <span className="flex items-center justify-center gap-2">
                      <Check className="w-5 h-5" />
                      Reviewed
                    </span>
                  ) : (
                    "I've Reviewed the Menu"
                  )}
                </button>
              </div>
            )}

            {/* ALLERGIES STEP */}
            {currentStep === 'allergies' && (
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Guest Alerts</h2>
                <p className="text-slate-500 mb-6">Allergies, VIPs, and special requests for your section</p>

                {/* Allergy Alerts */}
                {preShiftData?.guestAlerts.length ? (
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      Allergy Alerts
                    </h3>
                    <div className="space-y-3">
                      {preShiftData.guestAlerts.map((alert) => (
                        <div
                          key={alert.reservationId}
                          className="bg-red-50 border border-red-200 rounded-xl p-4"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-semibold text-slate-900">{alert.guestName}</p>
                              <p className="text-sm text-slate-600">
                                {alert.time} · Party of {alert.partySize}
                              </p>
                            </div>
                            {alert.isVIP && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                                VIP
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {alert.allergies.map((allergy, i) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium"
                              >
                                {allergy}
                              </span>
                            ))}
                          </div>
                          {alert.dietaryNotes && (
                            <p className="mt-2 text-sm text-red-700">{alert.dietaryNotes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                    <p className="text-emerald-700 text-center">
                      No allergy alerts for your section
                    </p>
                  </div>
                )}

                {/* VIP Guests */}
                {(preShiftData?.vipGuests?.length ?? 0) > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      VIP Guests
                    </h3>
                    <div className="space-y-3">
                      {preShiftData?.vipGuests.map((vip) => (
                        <div
                          key={vip.reservationId}
                          className="bg-amber-50 border border-amber-200 rounded-xl p-4"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Star className="w-4 h-4 text-amber-500" />
                            <span className="font-semibold text-slate-900">{vip.guestName}</span>
                          </div>
                          <p className="text-sm text-slate-600">
                            {vip.time} · Party of {vip.partySize}
                          </p>
                          {vip.notes && (
                            <p className="mt-2 text-sm text-amber-700">{vip.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reservations List */}
                {(preShiftData?.reservations?.length ?? 0) > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-500" />
                      Upcoming Reservations
                    </h3>
                    <div className="space-y-2">
                      {preShiftData?.reservations.map((res) => (
                        <div
                          key={res.id}
                          className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2"
                        >
                          <div>
                            <p className="font-medium text-slate-900">{res.guestName}</p>
                            <p className="text-xs text-slate-500">
                              {res.specialRequests || 'No special requests'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-slate-900">{res.time}</p>
                            <p className="text-xs text-slate-500">
                              <Users className="w-3 h-3 inline" /> {res.partySize}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Acknowledge */}
                <button
                  onClick={() => {
                    updateChecklist({ allergiesReviewed: true });
                    handleNext();
                  }}
                  className={clsx(
                    'w-full mt-6 py-4 rounded-xl font-semibold text-lg transition-all',
                    checklist.allergiesReviewed
                      ? 'bg-emerald-500 text-white'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  )}
                >
                  {checklist.allergiesReviewed ? (
                    <span className="flex items-center justify-center gap-2">
                      <Check className="w-5 h-5" />
                      Acknowledged
                    </span>
                  ) : (
                    "I've Reviewed Guest Alerts"
                  )}
                </button>
              </div>
            )}

            {/* SECTION STEP */}
            {currentStep === 'section' && (
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Your Section</h2>
                <p className="text-slate-500 mb-6">Confirm or select your section assignment</p>

                {preShiftData?.assignedSection ? (
                  // Already assigned - just confirm
                  <div>
                    <div className="bg-emerald-50 border-2 border-emerald-500 rounded-2xl p-6 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-8 h-8 rounded-full"
                          style={{ backgroundColor: preShiftData.assignedSection.color }}
                        />
                        <div>
                          <p className="font-bold text-xl text-slate-900">
                            {preShiftData.assignedSection.name}
                          </p>
                          <p className="text-sm text-slate-600">
                            {preShiftData.assignedSection.tables.length} tables
                          </p>
                        </div>
                      </div>

                      {/* Tables Grid */}
                      <div className="grid grid-cols-4 gap-2">
                        {preShiftData.assignedSection.tables.map((table) => (
                          <div
                            key={table.id}
                            className="bg-white rounded-lg p-2 text-center"
                          >
                            <p className="font-bold text-slate-900">T{table.number}</p>
                            <p className="text-xs text-slate-500">
                              <Users className="w-3 h-3 inline" /> {table.capacity}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        updateChecklist({ sectionConfirmed: true });
                        handleNext();
                      }}
                      className={clsx(
                        'w-full py-4 rounded-xl font-semibold text-lg transition-all',
                        checklist.sectionConfirmed
                          ? 'bg-emerald-500 text-white'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                      )}
                    >
                      {checklist.sectionConfirmed ? (
                        <span className="flex items-center justify-center gap-2">
                          <Check className="w-5 h-5" />
                          Confirmed
                        </span>
                      ) : (
                        'Confirm My Section'
                      )}
                    </button>
                  </div>
                ) : (
                  // No assignment - select section
                  <div>
                    {(!preShiftData?.availableSections || preShiftData.availableSections.length === 0) ? (
                      // No sections available
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <AlertCircle className="w-8 h-8 text-amber-600" />
                        </div>
                        <p className="text-slate-700 font-medium mb-2">No Sections Available</p>
                        <p className="text-slate-500 text-sm mb-6">
                          No sections have been set up yet or you weren't assigned to this shift.
                          You can continue without a section.
                        </p>
                        <button
                          onClick={() => {
                            updateChecklist({ sectionConfirmed: true });
                            handleNext();
                          }}
                          className="w-full py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700"
                        >
                          Continue Without Section
                        </button>
                      </div>
                    ) : preShiftData.availableSections.every((s: any) => !!s.currentServerId) ? (
                      // All sections are assigned
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Users className="w-8 h-8 text-amber-600" />
                        </div>
                        <p className="text-slate-700 font-medium mb-2">All Sections Assigned</p>
                        <p className="text-slate-500 text-sm mb-6">
                          All sections already have servers assigned. You can wait for one to become available
                          or ask your manager to reassign.
                        </p>
                        <button
                          onClick={() => {
                            updateChecklist({ sectionConfirmed: true });
                            handleNext();
                          }}
                          className="w-full py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700"
                        >
                          Continue Without Section
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3 mb-6">
                          {preShiftData.availableSections.map((section: any) => {
                            const isSelected = selectedSectionId === section.id;
                            const isAssigned = !!section.currentServerId;

                            return (
                              <button
                                key={section.id}
                                onClick={() => !isAssigned && handleSelectSection(section.id)}
                                disabled={isAssigned || assignSectionMutation.isPending}
                                className={clsx(
                                  'w-full p-4 rounded-xl border-2 transition-all text-left',
                                  isSelected && 'border-primary-500 bg-primary-50',
                                  !isSelected && !isAssigned && 'border-slate-200 hover:border-slate-300',
                                  isAssigned && 'border-slate-200 bg-slate-50 opacity-60'
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-6 h-6 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: section.color }}
                                  />
                                  <div className="flex-1">
                                    <p className="font-semibold text-slate-900">{section.name}</p>
                                    <p className="text-sm text-slate-500">
                                      {section.tableCount} tables
                                    </p>
                                  </div>
                                  {isSelected && (
                                    <Check className="w-5 h-5 text-primary-600" />
                                  )}
                                  {isAssigned && section.assignedServerName && (
                                    <div className="flex items-center gap-1 text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full">
                                      <Users className="w-3 h-3" />
                                      <span>{section.assignedServerName}</span>
                                    </div>
                                  )}
                                  {isAssigned && !section.assignedServerName && (
                                    <span className="text-xs text-slate-400">Assigned</span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {selectedSectionId && checklist.sectionConfirmed && (
                          <button
                            onClick={handleNext}
                            className="w-full py-4 bg-emerald-500 text-white rounded-xl font-semibold text-lg"
                          >
                            <span className="flex items-center justify-center gap-2">
                              <Check className="w-5 h-5" />
                              Section Confirmed
                            </span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* RESERVATIONS STEP (Host) */}
            {currentStep === 'reservations' && (
              <ReservationsStep
                reservations={(preShiftData?.reservations || []).map(r => ({
                  ...r,
                  isVIP: preShiftData?.vipGuests?.some(v => v.reservationId === r.id),
                  allergies: preShiftData?.guestAlerts?.find(a => a.reservationId === r.id)?.allergies,
                }))}
                isReviewed={checklist.reservationsReviewed}
                onAcknowledge={() => {
                  updateChecklist({ reservationsReviewed: true });
                  handleNext();
                }}
              />
            )}

            {/* KITCHEN STEP (Cook) */}
            {currentStep === 'kitchen' && (
              <KitchenStep
                eightySixItems={preShiftData?.menu.eightySixItems || []}
                limitedItems={preShiftData?.menu.limitedItems || []}
                isEightySixReviewed={checklist.eightySixReviewed}
                isStationConfirmed={checklist.stationConfirmed}
                selectedStationId={selectedStationId}
                onAcknowledge86={() => {
                  updateChecklist({ eightySixReviewed: true });
                }}
                onSelectStation={(stationId) => {
                  selectStation(stationId);
                }}
                onConfirmStation={() => {
                  updateChecklist({ stationConfirmed: true });
                  handleNext();
                }}
              />
            )}

            {/* OVERVIEW STEP (Manager) */}
            {currentStep === 'overview' && (
              <OverviewStep
                staffOnClock={(preShiftData?.activeStaff || []).map(s => ({
                  id: s.employeeId,
                  name: s.name,
                  role: s.role,
                  clockedIn: true,
                }))}
                staffScheduled={[]} // Could be populated from schedule API if available
                totalReservations={preShiftData?.reservations?.length || 0}
                expectedCovers={preShiftData?.reservations?.reduce((sum, r) => sum + r.partySize, 0) || 0}
                lowStockAlerts={preShiftData?.menu.limitedItems.length || 0}
                eightySixCount={preShiftData?.menu.eightySixItems.length || 0}
                isReviewed={checklist.overviewReviewed}
                onAcknowledge={() => {
                  updateChecklist({ overviewReviewed: true });
                  handleNext();
                }}
              />
            )}

            {/* START STEP */}
            {currentStep === 'start' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Ready to Start?</h2>
                <p className="text-slate-500 mb-6">
                  Review your checklist and begin your shift
                </p>

                {/* Checklist Summary - Role-specific */}
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <h3 className="font-semibold text-slate-700 mb-3">Pre-Shift Checklist</h3>
                  <div className="space-y-2">
                    {roleConfig.checklistItems.map((item) => {
                      const labels: Record<string, string> = {
                        menuReviewed: 'Menu reviewed',
                        allergiesReviewed: 'Guest alerts acknowledged',
                        sectionConfirmed: 'Section confirmed',
                        reservationsReviewed: 'Reservations reviewed',
                        eightySixReviewed: '86\'d list acknowledged',
                        stationConfirmed: 'Station confirmed',
                        overviewReviewed: 'Overview reviewed',
                      };
                      const isComplete = checklist[item];
                      return (
                        <div key={item} className="flex items-center gap-3">
                          <div className={clsx(
                            'w-6 h-6 rounded-full flex items-center justify-center',
                            isComplete ? 'bg-emerald-500' : 'bg-slate-300'
                          )}>
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <span className={clsx(
                            isComplete ? 'text-slate-900' : 'text-slate-400'
                          )}>
                            {labels[item] || item}
                          </span>
                        </div>
                      );
                    })}
                    {roleConfig.checklistItems.length === 0 && (
                      <p className="text-slate-500 text-sm">No checklist items required</p>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-6 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleStartShift}
                  disabled={!isChecklistComplete(employeeRole) || startShiftMutation.isPending}
                  className={clsx(
                    'w-full py-4 rounded-xl font-semibold text-lg transition-all',
                    isChecklistComplete(employeeRole)
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  )}
                >
                  {startShiftMutation.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Starting Shift...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Clock className="w-5 h-5" />
                      Start My Shift
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Buttons — flex-shrink-0 so the acknowledge/continue button
          is never squeezed off-screen by tall step content. */}
      <div className="px-4 pb-4 flex gap-3 flex-shrink-0">
        {currentStepIndex > 0 && (
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-white/20 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
        )}
        {currentStepIndex < STEPS.length - 1 && currentStep !== 'welcome' && (
          <button
            onClick={handleNext}
            className="flex-1 py-3 bg-white text-primary-700 rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
        {currentStep === 'welcome' && (
          <button
            onClick={handleNext}
            className="w-full py-3 bg-white text-primary-700 rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            Let's Get Started
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
